import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/gate-check/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { FilePdf, FileXls, MagnifyingGlass } from "@phosphor-icons/react";

export default function Reports() {
    const [gates, setGates] = useState([]);
    const [visitTypes, setVisitTypes] = useState([]);
    const [status, setStatus] = useState("all");
    const [gateId, setGateId] = useState("all");
    const [visitType, setVisitType] = useState("all");
    const [q, setQ] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [downloading, setDownloading] = useState(null);

    useEffect(() => {
        api.get("/gate-check/gates").then((r) => setGates(r.data));
        api.get("/gate-check/lookups/visit-types").then((r) => setVisitTypes(r.data?.values || [])).catch(() => {});
    }, []);

    const buildParams = () => {
        const p = new URLSearchParams();
        if (status !== "all") p.set("status", status);
        if (gateId !== "all") p.set("gate_id", gateId);
        if (visitType !== "all") p.set("visit_type", visitType);
        if (q) p.set("q", q);
        if (fromDate) p.set("from_date", fromDate);
        if (toDate) p.set("to_date", toDate);
        return p.toString();
    };

    // Quick presets — all end at today.
    const iso = (d) => d.toISOString().slice(0, 10);
    const applyPreset = (preset) => {
        const today = new Date();
        if (preset === "all") { setFromDate(""); setToDate(""); return; }
        if (preset === "today") { setFromDate(iso(today)); setToDate(iso(today)); return; }
        if (preset === "7d") {
            const from = new Date(today); from.setDate(from.getDate() - 6);
            setFromDate(iso(from)); setToDate(iso(today)); return;
        }
        if (preset === "month") {
            const from = new Date(today.getFullYear(), today.getMonth(), 1);
            setFromDate(iso(from)); setToDate(iso(today)); return;
        }
    };

    const rangeLabel = fromDate || toDate
        ? `${fromDate || "…"}_to_${toDate || "…"}`
        : "all";

    const download = async (kind) => {
        setDownloading(kind);
        try {
            const url = `/gate-check/reports/visits.${kind}?${buildParams()}`;
            const res = await api.get(url, { responseType: "blob" });
            const blob = new Blob([res.data]);
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = `visitors_${rangeLabel}.${kind}`;
            link.click();
            URL.revokeObjectURL(link.href);
            toast.success(`Downloaded ${kind.toUpperCase()}`);
        } catch (e) { toast.error("Download failed"); }
        finally { setDownloading(null); }
    };

    return (
        <div>
            <PageHeader
                overline="Exports"
                title="Reports"
                subtitle="Generate audit-ready visitor logs as PDF or Excel, filtered by date range, status, gate or search."
            />
            <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="tech-card p-6 lg:col-span-2 space-y-4">
                    <div className="font-display text-lg font-bold tracking-tight">Filters</div>
                    <div className="flex flex-wrap items-center gap-1.5">
                        {[
                            { key: "all", label: "All time" },
                            { key: "today", label: "Today" },
                            { key: "7d", label: "Last 7 days" },
                            { key: "month", label: "This month" },
                        ].map((p) => (
                            <button
                                key={p.key}
                                type="button"
                                onClick={() => applyPreset(p.key)}
                                data-testid={`preset-${p.key}`}
                                className="px-3 py-1.5 rounded-full border border-slate-200 text-[12px] font-semibold text-slate-600 hover:border-brand hover:text-brand transition-colors"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="overline text-[10px] block mb-1.5">From Date</label>
                            <Input type="date" value={fromDate} max={toDate || undefined}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="h-11" data-testid="input-report-from" />
                        </div>
                        <div>
                            <label className="overline text-[10px] block mb-1.5">To Date</label>
                            <Input type="date" value={toDate} min={fromDate || undefined}
                                onChange={(e) => setToDate(e.target.value)}
                                className="h-11" data-testid="input-report-to" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
                        <div>
                            <label className="overline text-[10px] block mb-1.5">Status</label>
                            <Select value={status} onValueChange={setStatus}>
                                <SelectTrigger className="h-11" data-testid="select-report-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {["all", "pending", "approved", "checked_in", "checked_out", "rejected"].map((s) => (
                                        <SelectItem key={s} value={s}>{s.replace("_", " ").toUpperCase()}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="overline text-[10px] block mb-1.5">Visit Type</label>
                            <Select value={visitType} onValueChange={setVisitType}>
                                <SelectTrigger className="h-11" data-testid="select-report-visit-type"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">ALL TYPES</SelectItem>
                                    {visitTypes.map((v) => (
                                        <SelectItem key={v} value={v} data-testid={`option-report-vt-${v}`}>{v}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="overline text-[10px] block mb-1.5">Gate</label>
                            <Select value={gateId} onValueChange={setGateId}>
                                <SelectTrigger className="h-11" data-testid="select-report-gate"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">ALL GATES</SelectItem>
                                    {gates.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="overline text-[10px] block mb-1.5">Search</label>
                            <div className="relative">
                                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} className="h-11 pl-9"
                                    placeholder="Name, phone…" data-testid="input-report-q" />
                            </div>
                        </div>
                    </div>
                </Card>

                <Card className="tech-card p-6 flex flex-col justify-between gap-3">
                    <div>
                        <div className="overline mb-1">Export</div>
                        <div className="font-display text-lg font-bold tracking-tight">Download Report</div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <Button
                            onClick={() => download("pdf")}
                            disabled={downloading === "pdf"}
                            data-testid="btn-export-pdf"
                            className="h-16 flex-col bg-red-700 hover:bg-red-800 text-white gap-1 press-effect"
                        >
                            <FilePdf size={22} weight="duotone" />
                            <span className="text-xs font-semibold">PDF</span>
                        </Button>
                        <Button
                            onClick={() => download("xlsx")}
                            disabled={downloading === "xlsx"}
                            data-testid="btn-export-xlsx"
                            className="h-16 flex-col bg-emerald-700 hover:bg-emerald-800 text-white gap-1 press-effect"
                        >
                            <FileXls size={22} weight="duotone" />
                            <span className="text-xs font-semibold">Excel</span>
                        </Button>
                    </div>
                </Card>
            </div>
        </div>
    );
}
